import { useState } from 'react';
import { useAdminData } from '../context/AdminDataContext';
import {
  Plus, Search, Edit2, Trash2,
  Eye, EyeOff, X, ChevronLeft, ChevronRight, Star
} from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 6;

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange && onChange(star)}
          className={`transition-colors ${star <= value ? 'text-amber-400' : 'text-gray-300'} ${onChange ? 'cursor-pointer hover:text-amber-300' : 'cursor-default'}`}
        >
          <Star className="w-5 h-5 fill-current" />
        </button>
      ))}
    </div>
  );
}

function TestimonialModal({ onClose, onSave, initial }) {
  const [form, setForm] = useState(initial || {
    name: '', designation: '', project: '', content: '', rating: 5, image: '', visible: true, featured: false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.content) { toast.error('Name and review are required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
      toast.success(initial ? 'Testimonial updated!' : 'Testimonial added!');
    } catch (err) {
      toast.error(err.message || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h3 className="text-slate-700 font-semibold font-outfit">{initial ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Client Image URL</label>
            <div className="flex gap-3">
              {form.image && <img src={form.image} alt="" className="w-12 h-12 rounded-full object-cover border border-gray-200" />}
              <input
                type="url" placeholder="https://..." value={form.image}
                onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Client Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rajesh Sharma"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]" />
            </div>
            <div>
              <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Designation</label>
              <input type="text" value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="e.g. Business Owner"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]" />
            </div>
          </div>
          <div>
            <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Project Name</label>
            <input type="text" value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} placeholder="e.g. Modern Arch Heights"
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]" />
          </div>
          <div>
            <label className="block text-slate-500 text-xs font-manrope font-medium mb-2">Rating</label>
            <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
          </div>
          <div>
            <label className="block text-slate-500 text-xs font-manrope font-medium mb-1">Review *</label>
            <textarea required rows={4} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Client review..."
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2] resize-none" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.visible} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} className="w-4 h-4 accent-[#47ace2]" />
              <span className="text-slate-600 text-sm font-manrope">Visible</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-amber-400" />
              <span className="text-slate-600 text-sm font-manrope">Featured</span>
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-slate-500 hover:bg-gray-50 font-manrope text-sm transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#47ace2] hover:bg-[#1f7db3] text-white font-manrope text-sm font-medium transition-colors disabled:opacity-60 shadow-sm">
              {saving ? 'Saving...' : (initial ? 'Update' : 'Add Testimonial')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TestimonialsPage() {
  const { testimonials, addTestimonial, updateTestimonial, deleteTestimonial } = useAdminData();
  const [search, setSearch] = useState('');
  const [filterVisible, setFilterVisible] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);

  const filtered = testimonials.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase());
    const matchVis = filterVisible === 'all' || (filterVisible === 'visible' ? t.visible !== false : t.visible === false);
    const matchFeat = filterFeatured === 'all' || (filterFeatured === 'featured' ? t.featured : !t.featured);
    return matchSearch && matchVis && matchFeat;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this testimonial?')) {
      try {
        await deleteTestimonial(id);
        toast.success('Testimonial deleted');
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  const handleToggleVisible = async (t) => {
    try {
      await updateTestimonial(t.id, { visible: !t.visible });
      toast.success(`Testimonial ${t.visible ? 'hidden' : 'shown'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update visibility');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-outfit">Testimonials</h1>
          <p className="text-slate-400 font-manrope text-sm mt-1">{testimonials.length} total reviews</p>
        </div>
        <button onClick={() => setModal('add')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl font-manrope text-sm font-medium transition-colors shadow-md shadow-[#47ace2]/25">
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-wrap gap-3 items-center shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search testimonials..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]" />
        </div>
        <select value={filterVisible} onChange={e => { setFilterVisible(e.target.value); setPage(1); }}
          className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          <option value="all">All Visibility</option>
          <option value="visible">Visible</option>
          <option value="hidden">Hidden</option>
        </select>
        <select value={filterFeatured} onChange={e => { setFilterFeatured(e.target.value); setPage(1); }}
          className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          <option value="all">All Types</option>
          <option value="featured">Featured</option>
          <option value="regular">Regular</option>
        </select>
      </div>

      {/* Cards */}
      {paginated.length === 0 ? (
        <div className="text-center py-20 text-slate-400 font-manrope">
          <Star className="w-12 h-12 mx-auto mb-3 opacity-25" />
          <p>No testimonials found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {paginated.map(t => (
            <div key={t.id} className={`bg-white border rounded-2xl p-5 flex flex-col gap-4 transition-all hover:shadow-md hover:border-[#47ace2]/20 shadow-sm ${t.visible === false ? 'opacity-50 border-gray-100' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {t.image ? (
                    <img src={t.image} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-gray-200" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#47ace2]/10 flex items-center justify-center text-[#47ace2] font-bold font-outfit">{t.name[0]}</div>
                  )}
                  <div>
                    <p className="text-slate-700 font-manrope font-semibold text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs font-manrope">{t.designation}</p>
                  </div>
                </div>
                {t.featured && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 text-xs rounded-lg font-manrope font-medium">Featured</span>}
              </div>
              <StarRating value={t.rating} />
              <p className="text-slate-500 text-sm font-manrope leading-relaxed flex-1 line-clamp-3">{t.content}</p>
              {t.project && <p className="text-slate-400 text-xs font-manrope">Project: <span className="text-slate-500 font-medium">{t.project}</span></p>}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setModal(t)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-slate-500 hover:text-slate-700 rounded-lg text-xs font-manrope transition-colors">
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => handleToggleVisible(t)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-slate-500 hover:text-slate-700 rounded-lg text-xs font-manrope transition-colors">
                  {t.visible === false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {t.visible === false ? 'Show' : 'Hide'}
                </button>
                <button onClick={() => handleDelete(t.id)} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-500 rounded-lg text-xs font-manrope transition-colors">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-xl text-sm font-manrope transition-colors ${p === page ? 'bg-[#47ace2] text-white shadow-sm' : 'bg-white border border-gray-200 text-slate-500 hover:text-slate-700'}`}>{p}</button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {modal && (
        <TestimonialModal
          initial={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={async (data) => {
            if (modal === 'add') return addTestimonial(data);
            else return updateTestimonial(modal.id, data);
          }}
        />
      )}
    </div>
  );
}
